/**
 * Department Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */

import React, { useEffect, useState } from 'react';
import { InlineFilterBar, InlineMultiSelect } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';

// Types
import { Company } from '../../../types';
import { SafetyCharacteristicType } from '../../../types/department';
import { companyService } from '../../../services/companyService';
import { useCompanyContext } from '../../../hooks/useCompanyContext';

interface DepartmentFiltersData {
  searchTerm: string;
  companyIds: string[];
  safetyCharacteristics: SafetyCharacteristicType[];
}

interface DepartmentFiltersProps {
  onFilter: (filters: DepartmentFiltersData) => void;
  loading?: boolean;
  preSelectedCompanyId?: string;
  total?: number;
}

// Options
const SAFETY_CHARACTERISTICS_OPTIONS = [
  { value: SafetyCharacteristicType.FOOD_HANDLING, label: 'Manipulação de Alimentos' },
  { value: SafetyCharacteristicType.ELECTRICAL, label: 'Trabalho com Eletricidade' },
  { value: SafetyCharacteristicType.PEMT, label: 'PEMT - Plataforma Elevatória' },
  { value: SafetyCharacteristicType.CONFINED_SPACE, label: 'Espaço Confinado' },
  { value: SafetyCharacteristicType.HEIGHT_WORK, label: 'Trabalho em Altura' },
  { value: SafetyCharacteristicType.HOT_WORK, label: 'Trabalho a Quente' },
  { value: SafetyCharacteristicType.FORKLIFT, label: 'Empilhadeira' },
  { value: SafetyCharacteristicType.SELF_PROPELLED_MACHINE, label: 'Máquina Autopropelida' },
];

const DepartmentFilters: React.FC<DepartmentFiltersProps> = ({
  onFilter,
  loading = false,
  preSelectedCompanyId,
  total,
}) => {
  const { shouldShowCompanyField, effectiveCompanyId } = useCompanyContext();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  useEffect(() => {
    const loadCompanies = async () => {
      setCompaniesLoading(true);
      try {
        const response = await companyService.getCompanies({ limit: 1000 });
        setCompanies(response.data);
      } catch (error) {
        console.error('Error loading companies:', error);
      } finally {
        setCompaniesLoading(false);
      }
    };
    loadCompanies();
  }, []);

  const defaultValues: DepartmentFiltersData = {
    searchTerm: '',
    companyIds: preSelectedCompanyId ? [preSelectedCompanyId] : [],
    safetyCharacteristics: [],
  };

  // When the company selector is hidden (single-binding user or a company
  // selected via header), force that company instead of whatever is in
  // state — mirrors the previous popover's handleApplyFilters behavior.
  const applyFilters = (raw: DepartmentFiltersData) => {
    const activeCompanyIds =
      !shouldShowCompanyField && effectiveCompanyId
        ? [effectiveCompanyId.toString()]
        : raw.companyIds;

    onFilter({ ...raw, companyIds: activeCompanyIds });
  };

  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter: applyFilters,
    live: true,
    labels: {
      companyIds: 'Empresas',
      safetyCharacteristics: 'Atividades Especiais',
    },
    formatValue: {
      companyIds: (v: string[]) =>
        v
          .map((id) => {
            const company = companies.find((c) => c.id === id);
            return company?.tradeName || company?.legalName || id;
          })
          .join(', '),
      safetyCharacteristics: (v: SafetyCharacteristicType[]) =>
        v
          .map((val) => SAFETY_CHARACTERISTICS_OPTIONS.find((o) => o.value === val)?.label ?? val)
          .join(', '),
    },
  });

  const COMPANY_OPTIONS = companies.map((company) => ({
    value: company.id,
    label: company.tradeName || company.legalName,
  }));

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome ou unidade"
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="setores"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      {shouldShowCompanyField && (
        <InlineMultiSelect
          label="Empresas"
          value={filters.companyIds}
          options={COMPANY_OPTIONS}
          onChange={(v) => setFilter('companyIds', v)}
          minWidth={160}
          disabled={companiesLoading}
        />
      )}
      <InlineMultiSelect
        label="Atividades Especiais (NR)"
        value={filters.safetyCharacteristics}
        options={SAFETY_CHARACTERISTICS_OPTIONS}
        onChange={(v) => setFilter('safetyCharacteristics', v as SafetyCharacteristicType[])}
        minWidth={220}
      />
    </InlineFilterBar>
  );
};

export default DepartmentFilters;
